import { supabase } from '@/lib/supabase';
import { ticketService } from '@/services/ticketService';
import { TicketPriority, TicketCategory } from '@/types/ticket';
import { knowledgeSourceSettingsService } from './knowledgeSourceSettings';
import { retrieveKnowledge } from './knowledgeRetrievalService';
import { parseSSEEvent, buildLiveData } from './aiChatHelpers';

// AIC-2.3: the assistant now calls the Claude backend (POST /api/v1/ai-chat).
// Data gathering stays here on the frontend under the user's session (RLS);
// the backend assembles the prompt + calls Claude. No OpenAI in the live path.

export interface ChatSession {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  title?: string;
  is_active: boolean;
  last_message_at?: string;
}

export interface ChatMessage {
  id: string;
  session_id: string;
  user_id: string;
  message: string;
  response?: string;
  created_at: string;
  metadata?: any;
  message_type?: 'text' | 'system' | 'error';
}

export class AIChatService {
  private cleanMarkdownFormatting(text: string): string {
    return text
      // Remove bold formatting (**text** or __text__)
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      // Remove italic formatting (*text* or _text_)
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      // Remove code formatting (`text`)
      .replace(/`(.*?)`/g, '$1')
      // Remove headers (# ## ###) but preserve the text with line breaks
      .replace(/^#{1,6}\s+(.+)$/gm, '$1\n')
      // Convert list markers (- * +) to bullet points but preserve line structure
      .replace(/^[\s]*[-\*\+]\s+/gm, '• ')
      // Convert numbered list markers (1. 2. etc) to bullet points
      .replace(/^[\s]*\d+\.\s+/gm, '• ')
      // Preserve double line breaks as paragraph breaks
      .replace(/\n\s*\n/g, '\n\n')
      // Clean up excessive spaces on single lines but preserve line breaks
      .replace(/[ \t]+/g, ' ')
      // Remove leading/trailing spaces from each line but keep the line breaks
      .replace(/^[ \t]+|[ \t]+$/gm, '')
      .trim();
  }

  // Resolve a user's role using legacy-first pattern: users.role_id is the primary
  // source of truth for normal users; user_roles is the fallback for multi-tenant
  // Superadmin assignments. Returns { roleId, roleName } or nulls if unresolved.
  private async getUserRoleInfo(userId: string): Promise<{ roleId: number | null; roleName: string }> {
    const { data: userRow } = await supabase
      .from('users')
      .select('role_id, roles!inner(id, name)')
      .eq('id', userId)
      .maybeSingle();

    const rawRoles = (userRow as any)?.roles;
    const legacyRole = rawRoles
      ? (Array.isArray(rawRoles) ? rawRoles[0] : rawRoles)
      : null;
    if (legacyRole?.id) {
      return { roleId: legacyRole.id, roleName: legacyRole.name || 'unknown' };
    }

    const { data: roleData } = await supabase
      .from('user_roles')
      .select('roles(id, name)')
      .eq('user_id', userId)
      .maybeSingle();
    const fallbackRole = (roleData as any)?.roles;
    if (fallbackRole?.id) {
      return { roleId: fallbackRole.id, roleName: fallbackRole.name || 'unknown' };
    }

    return { roleId: null, roleName: 'unknown' };
  }

  private detectExplicitTicketRequest(message: string): boolean {
    // Only detect when user explicitly asks to create a ticket
    const explicitTicketKeywords = [
      'create ticket', 'create a ticket', 'new ticket', 'open ticket', 'submit ticket',
      'file a ticket', 'open a support ticket', 'submit a support ticket'
    ];
    
    return explicitTicketKeywords.some(keyword => 
      message.toLowerCase().includes(keyword)
    );
  }

  private detectDetailedTicketInfo(message: string, chatHistory: ChatMessage[]): boolean {
    // Check if this appears to be detailed ticket information in response to our guidance
    const recentMessages = chatHistory.slice(-4); // Look at last 4 messages
    const askedForDetails = recentMessages.some(msg => 
      msg.response && msg.response.includes('I\'d be happy to help you create a support ticket')
    );
    
    if (!askedForDetails) return false;
    
    // Check if message contains detailed information
    const hasDetail = message.length > 50 && (
      message.toLowerCase().includes('problem') ||
      message.toLowerCase().includes('issue') ||
      message.toLowerCase().includes('error') ||
      message.toLowerCase().includes('expected') ||
      message.toLowerCase().includes('trying to') ||
      message.toLowerCase().includes('steps') ||
      message.toLowerCase().includes('reproduce')
    );
    
    return hasDetail;
  }

  private detectFollowUpQuestion(message: string, chatHistory: ChatMessage[]): boolean {
    const messageLower = message.toLowerCase();
    
    // Check for follow-up indicators
    const followUpIndicators = [
      'what are their names',
      'what are the names',
      'list them',
      'show them',
      'which ones',
      'those projects',
      'these projects',
      'the projects',
      'their names',
      'tell me more',
      'more details',
      'more info',
      'what about them',
      'elaborate',
      'can you list',
      'list the',
      'name them'
    ];
    
    // Check for pronouns that reference previous context
    const referencePronouns = ['those', 'these', 'them', 'their', 'the same', 'above'];
    
    const hasFollowUpIndicator = followUpIndicators.some(indicator => 
      messageLower.includes(indicator)
    );
    
    const hasReferencePronoun = referencePronouns.some(pronoun => 
      messageLower.includes(pronoun)
    );
    
    // Check if recent messages discussed projects or tickets
    const recentlyDiscussedData = chatHistory.slice(-3).some(msg => 
      msg.response && (
        msg.response.includes('project') ||
        msg.response.includes('ticket') ||
        msg.response.includes('found') ||
        msg.response.includes('waiting') ||
        msg.response.includes('pending')
      )
    );
    
    return (hasFollowUpIndicator || hasReferencePronoun) && recentlyDiscussedData;
  }

  private getPreviousContextData(chatHistory: ChatMessage[]): { projects?: any[], tickets?: any[] } {
    // Look through recent messages for stored context data
    const recentMessages = chatHistory.slice(-5).reverse(); // Last 5 messages, most recent first
    
    for (const msg of recentMessages) {
      if (msg.metadata && (msg.metadata.projects || msg.metadata.tickets)) {
        return {
          projects: msg.metadata.projects || [],
          tickets: msg.metadata.tickets || []
        };
      }
    }
    
    return {};
  }

  private mapCategoryFromMessage(message: string): TicketCategory {
    const categoryMap: { [key: string]: TicketCategory } = {
      'bug': TicketCategory.SWA_REPORTING_BUG,
      'broken': TicketCategory.SWA_REPORTING_BUG,
      'error': TicketCategory.SWA_REPORTING_BUG,
      'not working': TicketCategory.SWA_REPORTING_BUG,
      'feature': TicketCategory.SWA_FEATURE_REQUEST,
      'enhancement': TicketCategory.SWA_FEATURE_REQUEST,
      'request': TicketCategory.SWA_FEATURE_REQUEST,
      'site survey': TicketCategory.SST_ISSUE,
      'survey tool': TicketCategory.SST_ISSUE,
      'sst': TicketCategory.SST_ISSUE,
      'door to door': TicketCategory.D2D_ISSUE,
      'd2d': TicketCategory.D2D_ISSUE,
      'team': TicketCategory.TEAM_ASSIGNMENT,
      'assignment': TicketCategory.TEAM_ASSIGNMENT,
      'missing data': TicketCategory.MISSING_PROJECT_DATA,
      'missing project': TicketCategory.MISSING_PROJECT_DATA
    };
    
    const messageLower = message.toLowerCase();
    for (const [keyword, category] of Object.entries(categoryMap)) {
      if (messageLower.includes(keyword)) {
        return category;
      }
    }
    
    return TicketCategory.GENERAL_SUPPORT_ISSUE;
  }

  private mapPriorityFromMessage(message: string): TicketPriority {
    const messageLower = message.toLowerCase();
    
    if (messageLower.includes('urgent') || messageLower.includes('critical') || messageLower.includes('immediate')) {
      return TicketPriority.URGENT;
    }
    if (messageLower.includes('high priority') || messageLower.includes('asap') || messageLower.includes('important')) {
      return TicketPriority.HIGH;
    }
    if (messageLower.includes('low priority') || messageLower.includes('when possible') || messageLower.includes('minor')) {
      return TicketPriority.LOW;
    }
    
    return TicketPriority.MEDIUM; // Default
  }

  private extractTicketTitle(message: string): string {
    // Try to extract a concise title from the message
    const sentences = message.split(/[.!?]+/);
    const firstSentence = sentences[0]?.trim();
    
    if (firstSentence && firstSentence.length > 0 && firstSentence.length <= 80) {
      // Clean up common conversational elements
      return firstSentence
        .replace(/^(i |can |could |please |help |create |submit |open |file )\w*/i, '')
        .replace(/\b(ticket|issue|bug|problem)\b/gi, '')
        .trim()
        .replace(/^(a |an |the )/i, '')
        .trim();
    }
    
    // Fallback: try to find the main subject
    const words = message.split(' ');
    if (words.length > 3) {
      return words.slice(0, 8).join(' ');
    }
    
    return message.length > 50 ? message.substring(0, 47) + '...' : message;
  }

  async createTicketFromMessage(message: string, userId: string): Promise<{ success: boolean; ticket?: any; error?: string }> {
    try {
      const title = this.extractTicketTitle(message);
      const priority = this.mapPriorityFromMessage(message);
      const category = this.mapCategoryFromMessage(message);
      
      const ticketInput = {
        title: title || 'Support Request',
        description: message,
        priority,
        category
      };
      
      console.log('AI Chat: Creating ticket with input:', ticketInput);
      
      const ticket = await ticketService.createTicket(ticketInput);
      
      return {
        success: true,
        ticket
      };
    } catch (error) {
      console.error('Error creating ticket from AI chat:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async createSession(userId: string): Promise<ChatSession | null> {
    try {
      const { data, error } = await supabase
        .from('chat_sessions')
        .insert({ 
          user_id: userId,
          title: `Chat ${new Date().toLocaleDateString()}`
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error creating chat session:', error);
        return null;
      }

      return data as unknown as ChatSession;
    } catch (error) {
      console.error('Error in createSession:', error);
      return null;
    }
  }

  async getOrCreateSession(userId: string): Promise<ChatSession | null> {
    try {
      // Get active session or create new one
      const { data: existing, error: fetchError } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error fetching session:', fetchError);
        return null;
      }
      
      if (existing) {
        // Update last message timestamp
        await supabase
          .from('chat_sessions')
          .update({ last_message_at: new Date().toISOString() })
          .eq('id', existing.id);
        return existing as unknown as ChatSession;
      }
      
      return this.createSession(userId);
    } catch (error) {
      console.error('Error in getOrCreateSession:', error);
      return null;
    }
  }

  async saveMessage(
    sessionId: string, 
    userId: string, 
    message: string, 
    response?: string,
    messageType: 'text' | 'system' | 'error' = 'text',
    contextData?: { projects?: any[], tickets?: any[] }
  ): Promise<ChatMessage | null> {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          session_id: sessionId,
          user_id: userId,
          message,
          response: response || null,
          message_type: messageType,
          metadata: contextData || {}
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error saving message:', error);
        return null;
      }
      
      // Update session last message timestamp
      await supabase
        .from('chat_sessions')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', sessionId);

      return data as unknown as ChatMessage;
    } catch (error) {
      console.error('Error in saveMessage:', error);
      return null;
    }
  }

  async getChatHistory(sessionId: string): Promise<ChatMessage[]> {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error fetching chat history:', error);
        return [];
      }

      return (data || []) as unknown as ChatMessage[];
    } catch (error) {
      console.error('Error in getChatHistory:', error);
      return [];
    }
  }

  async getChatHistoryPaginated(
    sessionId: string, 
    limit: number = 10, 
    offset: number = 0
  ): Promise<{ messages: ChatMessage[], total: number, hasMore: boolean }> {
    try {
      // Get total count first
      const { count: totalCount, error: countError } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('session_id', sessionId);

      if (countError) {
        console.error('Error fetching message count:', countError);
        return { messages: [], total: 0, hasMore: false };
      }

      const total = totalCount || 0;

      // For getting the latest messages, we need to order descending and then reverse
      // This gets us the last N messages when offset is 0
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      if (error) {
        console.error('Error fetching paginated chat history:', error);
        return { messages: [], total, hasMore: false };
      }
      
      // Reverse the messages to get chronological order
      const messages = (data || []).reverse() as unknown as ChatMessage[];
      const hasMore = offset + limit < total;

      return { messages, total, hasMore };
    } catch (error) {
      console.error('Error in getChatHistoryPaginated:', error);
      return { messages: [], total: 0, hasMore: false };
    }
  }

  async getChatMessageCount(sessionId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('session_id', sessionId);
      
      if (error) {
        console.error('Error fetching message count:', error);
        return 0;
      }
      
      return count || 0;
    } catch (error) {
      console.error('Error in getChatMessageCount:', error);
      return 0;
    }
  }

  async getUserContext(userId: string): Promise<any> {
    try {
      // Get user profile with tenant information
      const { data: userProfile } = await supabase
        .from('users')
        .select(`
          *,
          tenants!tenant_id(
            tenant_id,
            company_name
          )
        `)
        .eq('id', userId)
        .single();
      
      // Get user's role first to determine access level
      const { roleId, roleName } = await this.getUserRoleInfo(userId);

      // Get project count based on role
      let projectCount = 0;
      
      if (roleId === 1 || roleName === 'Admin' || roleName === 'Super Admin') {
        // Admins have access to all projects
        const { count: totalCount } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true });
        projectCount = totalCount || 0;
      } else {
        // Other users only see assigned projects or projects they follow
        const { count: assignedCount } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .or(`assigned_sales_rep_id.eq.${userId},assigned_internal_pm_id.eq.${userId},assigned_installer_pm_id.eq.${userId}`);
        projectCount = assignedCount || 0;
        
        // Also count projects they follow
        const { count: followCount } = await supabase
          .from('project_followers')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('related_entity_type', 'project');
        
        projectCount += (followCount || 0);
      }
      
      // Get pipeline statistics for more context with user-friendly names
      const pipelineStats: any = {};
      if (roleId === 1 || roleName === 'Admin') {
        // Get counts by pipeline for admins with pipeline names
        const { data: pipelineData } = await supabase
          .from('projects')
          .select(`
            pipeline_id,
            pipelines!pipeline_id(
              id,
              name
            )
          `)
          .not('pipeline_id', 'is', null);
        
        if (pipelineData) {
          const pipelineCounts = pipelineData.reduce((acc: any, project: any) => {
            const pipelineName = project.pipelines?.name || `Pipeline ${project.pipeline_id}`;
            acc[pipelineName] = (acc[pipelineName] || 0) + 1;
            return acc;
          }, {});
          pipelineStats.by_pipeline = pipelineCounts;
        }
      }
      
      // Resolve the ACTIVE tenant (superadmin tenant-switcher aware) rather than
      // only the user's home tenant, so the assistant uses the tenant the user is
      // currently in — both for its name and for tenant-scoped data lookups.
      let activeTenantId: string | undefined = userProfile?.tenant_id;
      let tenantName = (userProfile as { tenants?: { company_name?: string } } | null)?.tenants?.company_name || 'Joules';
      try {
        const { data: rpcTenantId } = await supabase.rpc('get_current_user_tenant_id');
        if (rpcTenantId && rpcTenantId !== userProfile?.tenant_id) {
          activeTenantId = rpcTenantId;
          const { data: activeTenant } = await supabase
            .from('tenants')
            .select('company_name')
            .eq('tenant_id', rpcTenantId)
            .single();
          if (activeTenant?.company_name) tenantName = activeTenant.company_name;
        }
      } catch (e) {
        console.warn('[aiChatService] could not resolve active tenant; using home tenant:', e);
      }
      const firstName = userProfile?.first_name || '';
      const lastName = userProfile?.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      return {
        user_id: userId,
        email: userProfile?.email,
        first_name: firstName,
        last_name: lastName,
        full_name: fullName,
        role: roleName,
        role_id: roleId,
        projects_count: projectCount,
        pipeline_stats: pipelineStats,
        tenant_name: tenantName,
        tenant_id: activeTenantId
      };
    } catch (error) {
      console.error('Error getting user context:', error);
      return { user_id: userId };
    }
  }

  async searchUserTickets(ticketFilter: string | null, userId: string): Promise<any[]> {
    try {
      // Get user's role to determine access
      const { roleId, roleName } = await this.getUserRoleInfo(userId);

      let query = supabase
        .from('tickets')
        .select(`
          id,
          ticket_number,
          title,
          description,
          status,
          priority,
          internal_priority,
          category,
          created_at,
          updated_at,
          due_date,
          created_by,
          assigned_to,
          project_id
        `)
        .order('updated_at', { ascending: false })
        .limit(20);
      
      // Apply filters based on user query
      if (ticketFilter) {
        console.log('AI Chat: Applying ticket filter:', ticketFilter);
        
        switch (ticketFilter.toLowerCase()) {
          case 'open':
            query = query.in('status', ['open', 'in_progress']);
            break;
          case 'closed':
          case 'resolved':
            query = query.eq('status', 'resolved');
            break;
          case 'urgent':
          case 'high priority':
            query = query.in('priority', ['urgent', 'high']);
            break;
          case 'assigned to me':
          case 'my tickets':
            query = query.eq('assigned_to', userId);
            break;
        }
      }
      
      // Apply access control based on role
      if (roleId !== 1 && roleName !== 'Admin' && roleName !== 'Super Admin') {
        // Non-admins can only see tickets they created or are assigned to
        query = query.or(`created_by.eq.${userId},assigned_to.eq.${userId}`);
      }
      
      const { data: tickets, error } = await query;
      
      if (error) {
        console.error('Error searching user tickets:', error);
        return [];
      }
      
      console.log(`AI Chat: Raw ticket search results: ${tickets?.length || 0} tickets found`);
      
      if (tickets && tickets.length > 0) {
        console.log('AI Chat: Ticket status breakdown:', tickets.reduce((acc: any, t: any) => {
          const status = t.status || 'Unknown';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {}));
        
        console.log('AI Chat: Ticket priority breakdown:', tickets.reduce((acc: any, t: any) => {
          const priority = t.priority || 'Unknown';
          acc[priority] = (acc[priority] || 0) + 1;
          return acc;
        }, {}));
      }
      
      if (!tickets || tickets.length === 0) {
        console.log('AI Chat: No tickets found for user:', userId);
        return [];
      }
      
      return tickets;
    } catch (error) {
      console.error('Error in searchUserTickets:', error);
      return [];
    }
  }

  async searchProjectsByStage(stageQuery: string, userId: string): Promise<any[]> {
    try {
      // Get user's role to determine access
      const { roleId, roleName } = await this.getUserRoleInfo(userId);

      // Map stage queries to exact stage name patterns (based on actual dashboard stages)
      const stageMapping: { [key: string]: string[] } = {
        'permit': ['Permits Pending', 'Pending Permit', 'permit'],
        'pending permit': ['Permits Pending', 'Pending Permit'],
        'pending permits': ['Permits Pending', 'Pending Permit'],
        'install': ['Ready to Schedule Install', 'Install Scheduled', 'Live Install', 'Install Complete'],
        'ready to schedule': ['Ready to Schedule Install'],
        'ready to install': ['Ready to Schedule Install'],
        'ready to schedule install': ['Ready to Schedule Install'],
        'design': ['Design', 'Engineering', 'design review'],
        'design review': ['Design', 'Engineering'],
        'contract': ['Contract Signed', 'contract'],
        'contract signed': ['Contract Signed'],
        'pto': ['PTO Pending', 'PTO Granted', 'pto'],
        'pto complete': ['PTO Granted'],
        'pto pending': ['PTO Pending'],
        'waiting for approval': ['approval', 'waiting', 'pending approval'],
        'new leads': ['New Leads'],
        'credit': ['Credit Failed', 'Credit Pending']
      };
      
      console.log('AI Chat: Stage query detected:', stageQuery.toLowerCase());
      console.log('AI Chat: Mapped stage terms:', stageMapping[stageQuery.toLowerCase()] || [stageQuery.toLowerCase()]);
      
      const searchTerms = stageMapping[stageQuery.toLowerCase()] || [stageQuery.toLowerCase()];
      
      let query = supabase
        .from('projects')
        .select(`
          id,
          project_name,
          company_name,
          current_stage_id,
          pipeline_id,
          project_street,
          project_city,
          project_state,
          project_zip_code,
          project_full_address,
          assigned_sales_rep_id,
          assigned_internal_pm_id,
          assigned_installer_pm_id,
          created_at,
          updated_at,
          stages:stages!current_stage_id(name),
          pipelines:pipelines!pipeline_id(name)
        `);
      
      // Build OR condition for stage names using proper filter syntax
      if (searchTerms.length === 1) {
        console.log('AI Chat: Searching for single stage term:', searchTerms[0]);
        query = query.filter('stages.name', 'ilike', `%${searchTerms[0]}%`);
      } else {
        // For multiple search terms, try each one individually and combine results
        console.log('AI Chat: Searching for multiple stage terms:', searchTerms);
        
        // Use the most specific match first, then fallback to broader terms
        let matchFound = false;
        for (const term of searchTerms) {
          if (term.length > 5) { // Prefer longer, more specific terms
            console.log('AI Chat: Trying specific stage term:', term);
            query = query.filter('stages.name', 'ilike', `%${term}%`);
            matchFound = true;
            break;
          }
        }
        
        // If no specific match, use the first term
        if (!matchFound) {
          console.log('AI Chat: Using first stage term as fallback:', searchTerms[0]);
          query = query.filter('stages.name', 'ilike', `%${searchTerms[0]}%`);
        }
      }
      
      // Apply access control based on role
      if (roleId !== 1 && roleName !== 'Admin' && roleName !== 'Super Admin') {
        // Non-admins can only see projects they're assigned to or follow
        
        // Get projects user follows
        const { data: followedProjects } = await supabase
          .from('project_followers')
          .select('related_entity_id')
          .eq('user_id', userId)
          .eq('related_entity_type', 'project');
        
        const followedIds = followedProjects?.map(f => f.related_entity_id) || [];
        
        // Apply additional access filters
        if (followedIds.length > 0) {
          query = query.or(`assigned_sales_rep_id.eq.${userId},assigned_internal_pm_id.eq.${userId},assigned_installer_pm_id.eq.${userId},id.in.(${followedIds.join(',')})`);
        } else {
          query = query.or(`assigned_sales_rep_id.eq.${userId},assigned_internal_pm_id.eq.${userId},assigned_installer_pm_id.eq.${userId}`);
        }
      }
      
      const { data: projects, error } = await query;
      
      if (error) {
        console.error('Error searching projects by stage:', error);
        return [];
      }
      
      console.log(`AI Chat: Raw stage search results: ${projects?.length || 0} projects found`);
      
      if (projects && projects.length > 0) {
        console.log('AI Chat: Stage breakdown:', projects.reduce((acc: any, p: any) => {
          const stage = p.stages?.name || 'Unknown';
          acc[stage] = (acc[stage] || 0) + 1;
          return acc;
        }, {}));
      }
      
      if (!projects || projects.length === 0) {
        console.log('AI Chat: No projects found for stage query:', stageQuery);
        return [];
      }
      
      // Enhance projects with task and checklist data (same as other searches)
      const enhancedProjects = await Promise.all(
        projects.map(async (project: any) => {
          try {
            // Get tasks for this project
            const { data: tasks } = await supabase
              .from('tasks')
              .select(`
                id,
                title,
                description,
                status,
                priority,
                due_date,
                assigned_to,
                created_at,
                updated_at,
                completed_at
              `)
              .eq('project_id', project.id)
              .order('created_at', { ascending: false })
              .limit(20);
            
            // Get checklist items for this project
            const { data: checklistItems } = await supabase
              .from('project_checklist_items')
              .select(`
                id,
                is_completed,
                completed_at,
                value_text,
                value_date,
                assigned_user_id,
                template_item:checklist_items!template_item_id(
                  id,
                  item_name,
                  item_type,
                  is_required
                )
              `)
              .eq('project_id', project.id)
              .order('is_completed', { ascending: true });
            
            // Group checklist items by completion status
            const incompleteChecklists = checklistItems?.filter(item => !item.is_completed) || [];
            const completedChecklists = checklistItems?.filter(item => item.is_completed) || [];
            
            // Get pending/blocked tasks for quick insights
            const pendingTasks = tasks?.filter(task => task.status === 'pending' || task.status === 'blocked') || [];
            const urgentTasks = tasks?.filter(task => task.priority === 'urgent' || task.priority === 'high') || [];
            
            return {
              ...project,
              tasks: tasks || [],
              checklist_items: checklistItems || [],
              incomplete_checklists: incompleteChecklists,
              completed_checklists: completedChecklists,
              pending_tasks: pendingTasks,
              urgent_tasks: urgentTasks,
              total_tasks: tasks?.length || 0,
              incomplete_checklist_count: incompleteChecklists.length,
              completed_checklist_count: completedChecklists.length
            };
          } catch (enhanceError) {
            console.error(`Error enhancing project ${project.id}:`, enhanceError);
            return project;
          }
        })
      );
      
      return enhancedProjects;
    } catch (error) {
      console.error('Error in searchProjectsByStage:', error);
      return [];
    }
  }

  async searchProjectsByLocation(location: string, userId: string): Promise<any[]> {
    try {
      // Get user's role to determine access
      const { roleId, roleName } = await this.getUserRoleInfo(userId);

      // Normalize location search terms (handle abbreviations)
      const locationMap: { [key: string]: string[] } = {
        'california': ['california', 'ca', 'calif'],
        'texas': ['texas', 'tx'],
        'florida': ['florida', 'fl', 'fla'],
        'new york': ['new york', 'ny', 'n.y.'],
        'arizona': ['arizona', 'az', 'ariz'],
        'nevada': ['nevada', 'nv', 'nev'],
        'utah': ['utah', 'ut'],
        'colorado': ['colorado', 'co', 'colo'],
        'oregon': ['oregon', 'or', 'ore'],
        'washington': ['washington', 'wa', 'wash']
      };
      
      // Find the full state name from the search term
      let stateToSearch = location.toLowerCase();
      for (const [fullName, variants] of Object.entries(locationMap)) {
        if (variants.includes(location.toLowerCase())) {
          stateToSearch = fullName;
          break;
        }
      }
      
      let query = supabase
        .from('projects')
        .select(`
          id,
          project_name,
          company_name,
          current_stage_id,
          pipeline_id,
          project_street,
          project_city,
          project_state,
          project_zip_code,
          project_full_address,
          assigned_sales_rep_id,
          assigned_internal_pm_id,
          assigned_installer_pm_id,
          created_at,
          updated_at,
          stages:stages!current_stage_id(name),
          pipelines:pipelines!pipeline_id(name)
        `)
        .ilike('project_state', `%${stateToSearch}%`);
      
      // Apply access control based on role
      if (roleId !== 1 && roleName !== 'Admin' && roleName !== 'Super Admin') {
        // Non-admins can only see projects they're assigned to or follow
        
        // Get projects user follows
        const { data: followedProjects } = await supabase
          .from('project_followers')
          .select('related_entity_id')
          .eq('user_id', userId)
          .eq('related_entity_type', 'project');
        
        const followedIds = followedProjects?.map(f => f.related_entity_id) || [];
        
        // Build OR condition for access
        const accessConditions = [
          `assigned_sales_rep_id.eq.${userId}`,
          `assigned_internal_pm_id.eq.${userId}`,
          `assigned_installer_pm_id.eq.${userId}`
        ];
        
        if (followedIds.length > 0) {
          accessConditions.push(`id.in.(${followedIds.join(',')})`)
        }
        
        query = query.or(accessConditions.join(','));
      }
      
      const { data: projects, error } = await query;
      
      if (error) {
        console.error('Error searching projects by location:', error);
        return [];
      }
      
      if (!projects || projects.length === 0) {
        return [];
      }
      
      // Enhance projects with task and checklist data (same as regular search)
      const enhancedProjects = await Promise.all(
        projects.map(async (project: any) => {
          try {
            // Get tasks for this project
            const { data: tasks } = await supabase
              .from('tasks')
              .select(`
                id,
                title,
                description,
                status,
                priority,
                due_date,
                assigned_to,
                created_at,
                updated_at,
                completed_at
              `)
              .eq('project_id', project.id)
              .order('created_at', { ascending: false })
              .limit(20);
            
            // Get checklist items for this project
            const { data: checklistItems } = await supabase
              .from('project_checklist_items')
              .select(`
                id,
                is_completed,
                completed_at,
                value_text,
                value_date,
                assigned_user_id,
                template_item:checklist_items!template_item_id(
                  id,
                  item_name,
                  item_type,
                  is_required
                )
              `)
              .eq('project_id', project.id)
              .order('is_completed', { ascending: true });
            
            // Group checklist items by completion status
            const incompleteChecklists = checklistItems?.filter(item => !item.is_completed) || [];
            const completedChecklists = checklistItems?.filter(item => item.is_completed) || [];
            
            // Get pending/blocked tasks for quick insights
            const pendingTasks = tasks?.filter(task => task.status === 'pending' || task.status === 'blocked') || [];
            const urgentTasks = tasks?.filter(task => task.priority === 'urgent' || task.priority === 'high') || [];
            
            return {
              ...project,
              tasks: tasks || [],
              checklist_items: checklistItems || [],
              incomplete_checklists: incompleteChecklists,
              completed_checklists: completedChecklists,
              pending_tasks: pendingTasks,
              urgent_tasks: urgentTasks,
              total_tasks: tasks?.length || 0,
              incomplete_checklist_count: incompleteChecklists.length,
              completed_checklist_count: completedChecklists.length
            };
          } catch (enhanceError) {
            console.error(`Error enhancing project ${project.id}:`, enhanceError);
            return project;
          }
        })
      );
      
      return enhancedProjects;
    } catch (error) {
      console.error('Error in searchProjectsByLocation:', error);
      return [];
    }
  }

  async searchProjects(searchTerm: string, userId: string): Promise<any[]> {
    try {
      // Get user's role to determine access
      const { roleId, roleName } = await this.getUserRoleInfo(userId);

      let query = supabase
        .from('projects')
        .select(`
          id,
          project_name,
          company_name,
          current_stage_id,
          pipeline_id,
          project_street,
          project_city,
          project_state,
          project_zip_code,
          project_full_address,
          assigned_sales_rep_id,
          assigned_internal_pm_id,
          assigned_installer_pm_id,
          created_at,
          updated_at,
          stages:stages!current_stage_id(name),
          pipelines:pipelines!pipeline_id(name)
        `)
        .or(`project_name.ilike.%${searchTerm}%,company_name.ilike.%${searchTerm}%,project_city.ilike.%${searchTerm}%,project_street.ilike.%${searchTerm}%,project_full_address.ilike.%${searchTerm}%`)
        .limit(10);
      
      // Apply access control based on role
      if (roleId !== 1 && roleName !== 'Admin' && roleName !== 'Super Admin') {
        // Non-admins can only see projects they're assigned to or follow
        
        // Get projects user follows
        const { data: followedProjects } = await supabase
          .from('project_followers')
          .select('related_entity_id')
          .eq('user_id', userId)
          .eq('related_entity_type', 'project');
        
        const followedIds = followedProjects?.map(f => f.related_entity_id) || [];
        
        // Build OR condition for access
        const accessConditions = [
          `assigned_sales_rep_id.eq.${userId}`,
          `assigned_internal_pm_id.eq.${userId}`,
          `assigned_installer_pm_id.eq.${userId}`
        ];
        
        if (followedIds.length > 0) {
          accessConditions.push(`id.in.(${followedIds.join(',')})`);
        }
        
        query = query.or(accessConditions.join(','));
      }
      
      const { data: projects, error } = await query;
      
      if (error) {
        console.error('Error searching projects:', error);
        return [];
      }
      
      if (!projects || projects.length === 0) {
        return [];
      }
      
      // Enhance projects with task and checklist data
      const enhancedProjects = await Promise.all(
        projects.map(async (project: any) => {
          try {
            // Get tasks for this project
            const { data: tasks } = await supabase
              .from('tasks')
              .select(`
                id,
                title,
                description,
                status,
                priority,
                due_date,
                assigned_to,
                created_at,
                updated_at,
                completed_at
              `)
              .eq('project_id', project.id)
              .order('created_at', { ascending: false })
              .limit(20);
            
            // Get checklist items for this project (focus on incomplete ones)
            const { data: checklistItems } = await supabase
              .from('project_checklist_items')
              .select(`
                id,
                is_completed,
                completed_at,
                value_text,
                value_date,
                assigned_user_id,
                template_item:checklist_items!template_item_id(
                  id,
                  item_name,
                  item_type,
                  is_required
                )
              `)
              .eq('project_id', project.id)
              .order('is_completed', { ascending: true }); // Show incomplete items first
            
            // Group checklist items by completion status
            const incompleteChecklists = checklistItems?.filter(item => !item.is_completed) || [];
            const completedChecklists = checklistItems?.filter(item => item.is_completed) || [];
            
            // Get pending/blocked tasks for quick insights
            const pendingTasks = tasks?.filter(task => task.status === 'pending' || task.status === 'blocked') || [];
            const urgentTasks = tasks?.filter(task => task.priority === 'urgent' || task.priority === 'high') || [];
            
            return {
              ...project,
              tasks: tasks || [],
              checklist_items: checklistItems || [],
              incomplete_checklists: incompleteChecklists,
              completed_checklists: completedChecklists,
              pending_tasks: pendingTasks,
              urgent_tasks: urgentTasks,
              total_tasks: tasks?.length || 0,
              incomplete_checklist_count: incompleteChecklists.length,
              completed_checklist_count: completedChecklists.length
            };
          } catch (enhanceError) {
            console.error(`Error enhancing project ${project.id}:`, enhanceError);
            // Return project without enhancement if there's an error
            return project;
          }
        })
      );
      
      return enhancedProjects;
    } catch (error) {
      console.error('Error in searchProjects:', error);
      return [];
    }
  }

  /**
   * Stream a reply from the Claude backend (POST /api/v1/ai-chat). Accumulates
   * tokens and returns the full text; `onToken` (optional) gets each delta for
   * progressive rendering.
   */
  private async streamChatFromBackend(
    message: string,
    history: { role: string; content: string }[],
    context: Record<string, any>,
    onToken?: (delta: string) => void,
    onWidget?: (config: Record<string, any>) => void,
  ): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');

    const baseUrl = import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';
    const res = await fetch(`${baseUrl}/api/v1/ai-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ message, history, context }),
    });
    if (!res.ok || !res.body) {
      throw new Error(`ai-chat request failed: ${res.status}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let full = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() ?? ''; // keep the trailing partial block
      for (const block of blocks) {
        const evt = parseSSEEvent(block);
        if (!evt) continue;
        if (evt.type === 'token' && typeof evt.text === 'string') {
          full += evt.text;
          onToken?.(evt.text);
        } else if (evt.type === 'widget' && evt.config) {
          onWidget?.(evt.config as Record<string, any>);  // AIC-4.1 generated report
        } else if (evt.type === 'error') {
          throw new Error(evt.message || 'assistant error');
        }
        // evt.type === 'done' → model + usage (logged by AIC-3.1 later)
      }
    }
    return full;
  }

  async sendMessage(
    message: string,
    sessionId: string,
    userId: string,
    onToken?: (delta: string) => void,
    onWidget?: (config: Record<string, any>) => void
  ): Promise<string> {
    try {
      // Get user context for better responses
      const userContext = await this.getUserContext(userId);
      
      // Get recent chat history for context (needed for ticket creation logic)
      const history = await this.getChatHistory(sessionId);
      const recentHistory = history.slice(-10); // Last 10 messages for context
      
      // Knowledge retrieval now happens via AIC-1.1 (retrieveKnowledge) below,
      // just before the backend call — no OpenAI embedding search here (AC6).

      // Check if this is a follow-up question about previous data
      const isFollowUpQuestion = this.detectFollowUpQuestion(message, recentHistory);
      
      // Get previous context data if this is a follow-up
      let previousContextData: { projects?: any[], tickets?: any[] } = {};
      if (isFollowUpQuestion) {
        previousContextData = this.getPreviousContextData(recentHistory);
        console.log('AI Chat: Detected follow-up question, retrieved previous context:', {
          projectCount: previousContextData.projects?.length || 0,
          ticketCount: previousContextData.tickets?.length || 0,
          message: message,
          hasProjects: !!previousContextData.projects,
          hasTickets: !!previousContextData.tickets
        });
        
        // Log sample project data for debugging
        if (previousContextData.projects && previousContextData.projects.length > 0) {
          console.log('AI Chat: Sample project from context:', previousContextData.projects[0]);
        }
      }
      
      // Check if the message is asking about specific projects or locations
      let projectData = previousContextData.projects || null;
      let ticketData = previousContextData.tickets || null;
      
      const projectKeywords = ['project', 'michael', 'lettera', 'clery', 'company', 'address', 'status', 'stage', 'tell me about', 'what about', 'show me', 'find', 'holding up', 'what\'s needed', 'next steps', 'blockers', 'blocker', 'stuck', 'progress', 'complete', 'done', 'finish', 'remaining'];
      const locationKeywords = ['california', 'texas', 'florida', 'new york', 'arizona', 'nevada', 'utah', 'colorado', 'oregon', 'washington', 'state', 'city', 'location', 'where', 'how many'];
      const stageKeywords = ['pending', 'permit', 'permits', 'install', 'installation', 'ready', 'scheduled', 'waiting', 'approval', 'approved', 'signed', 'contract', 'design', 'review', 'pto', 'complete', 'completed', 'stage', 'pipeline', 'status'];
      const ticketKeywords = ['ticket', 'tickets', 'support', 'issue', 'bug', 'problem', 'help', 'request', 'assigned', 'urgent', 'resolved', 'open', 'closed'];
      
      const isAskingAboutProject = projectKeywords.some(keyword => 
        message.toLowerCase().includes(keyword)
      );
      
      const isAskingAboutLocation = locationKeywords.some(keyword => 
        message.toLowerCase().includes(keyword)
      );
      
      const isAskingAboutStage = stageKeywords.some(keyword => 
        message.toLowerCase().includes(keyword)
      );
      
      const isAskingAboutTickets = ticketKeywords.some(keyword => 
        message.toLowerCase().includes(keyword)
      );
      
      const hasExplicitTicketRequest = this.detectExplicitTicketRequest(message);
      const hasDetailedTicketInfo = this.detectDetailedTicketInfo(message, recentHistory);
      
      // If user has provided detailed ticket information, create the ticket
      if (hasDetailedTicketInfo) {
        console.log('AI Chat: User provided detailed ticket information, creating ticket');
        const ticketCreationResult = await this.createTicketFromMessage(message, userId);
        
        if (ticketCreationResult.success) {
          const ticket = ticketCreationResult.ticket;
          const successMessage = `Perfect! I've created ticket #${ticket.ticket_number} for you with all the details you provided.\n\nTicket Details:\n• Title: ${ticket.title}\n• Priority: ${ticket.priority}\n• Category: ${ticket.category}\n• Status: ${ticket.status}\n• Created: ${new Date(ticket.created_at).toLocaleDateString()}\n\nYour detailed ticket has been submitted to our support team. You'll receive updates as it's processed. You can view all your tickets by clicking the "Tickets" button in the chat header.`;
          
          await this.saveMessage(sessionId, userId, message, successMessage);
          return successMessage;
        } else {
          const errorMessage = `I tried to create a ticket for you, but encountered an error: ${ticketCreationResult.error || 'Unknown error'}. Please try again or contact support directly.`;
          await this.saveMessage(sessionId, userId, message, errorMessage, 'error');
          return errorMessage;
        }
      }
      
      // If user explicitly wants to create a ticket, ask for details
      if (hasExplicitTicketRequest) {
        const ticketCreationGuidance = `I'd be happy to help you create a support ticket. To make sure your issue gets resolved quickly, I'll need some additional information:\n\n• What specific problem are you experiencing?\n• What were you trying to do when the issue occurred?\n• What did you expect to happen?\n• What actually happened instead?\n• If it's a bug, what steps can someone follow to reproduce it?\n• How urgent is this issue? (low/medium/high/urgent)\n\nPlease provide as much detail as possible, and I'll create a well-documented ticket for you.`;
        
        await this.saveMessage(sessionId, userId, message, ticketCreationGuidance);
        return ticketCreationGuidance;
      }
      
      // Check if CRM data is enabled before searching
      const crmSettings = await knowledgeSourceSettingsService.getCRMSettings();
      const crmEnabled = await knowledgeSourceSettingsService.isSourceEnabled('crm_data');
      
      // Only fetch new data if CRM is enabled and we don't already have it from a follow-up question
      // Check specific toggles for projects vs tickets
      const shouldSearchProjects = crmEnabled && crmSettings.projects_enabled && 
        (isAskingAboutProject || isAskingAboutLocation || isAskingAboutStage);
      const shouldSearchTickets = crmEnabled && crmSettings.tickets_enabled && isAskingAboutTickets;
      
      if (!isFollowUpQuestion && (shouldSearchProjects || shouldSearchTickets)) {
        console.log('AI Chat: CRM enabled and user asking about data, message:', message, {
          crmEnabled,
          projectsEnabled: crmSettings.projects_enabled,
          ticketsEnabled: crmSettings.tickets_enabled,
          shouldSearchProjects,
          shouldSearchTickets
        });
        
        // Only search projects if projects toggle is enabled
        if (shouldSearchProjects) {
          // Extract potential search terms from the message
          // First try to find quoted terms
          const quotedTerms = message.match(/"([^"]+)"|'([^']+)'/g);
          if (quotedTerms && quotedTerms.length > 0) {
            const term = quotedTerms[0].replace(/["']/g, '');
            console.log('AI Chat: Searching with quoted term:', term);
            projectData = await this.searchProjects(term, userId);
          }
        }
        
        // If no quoted terms or no results, look for capitalized names  
        if (shouldSearchProjects && (!projectData || projectData.length === 0)) {
          // Look for consecutive capitalized words (proper names)
          const properNames = message.match(/[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*/g) || [];
          console.log('AI Chat: Found proper names:', properNames);
          
          // Try to find names, but skip common words like "Project", "Commercial", "Tell"
          const skipWords = ['Tell', 'Project', 'Commercial', 'What', 'Can', 'How'];
          for (const term of properNames) {
            if (term.length > 2 && !skipWords.includes(term)) {
              console.log('AI Chat: Searching with proper name:', term);
              const projects = await this.searchProjects(term, userId);
              if (projects.length > 0) {
                projectData = projects;
                break;
              }
            }
          }
        }
        
        // If still no results, try extracting key parts of the message
        if (shouldSearchProjects && (!projectData || projectData.length === 0)) {
          // Remove common words and search with the rest
          const cleanedMessage = message
            .replace(/\b(tell|me|about|the|project|for|what|is|show|find|get|can|you)\b/gi, '')
            .trim();
          
          console.log('AI Chat: Searching with cleaned message:', cleanedMessage);
          if (cleanedMessage.length > 2) {
            projectData = await this.searchProjects(cleanedMessage, userId);
          }
        }
        
        console.log('AI Chat: Final projectData:', projectData?.length || 0, 'results');
      }
      
      // If no project data found but asking about location, do a location-based search
      if (shouldSearchProjects && (!projectData || projectData.length === 0) && isAskingAboutLocation) {
        console.log('AI Chat: Performing location-based search');
        
        // Extract location from message
        const locationTerms = ['california', 'ca', 'texas', 'tx', 'florida', 'fl', 'new york', 'ny', 'arizona', 'az', 'nevada', 'nv', 'utah', 'ut', 'colorado', 'co', 'oregon', 'or', 'washington', 'wa'];
        let detectedLocation = null;
        
        for (const term of locationTerms) {
          if (message.toLowerCase().includes(term)) {
            detectedLocation = term;
            break;
          }
        }
        
        if (detectedLocation) {
          projectData = await this.searchProjectsByLocation(detectedLocation, userId);
          console.log('AI Chat: Location search results:', projectData?.length || 0, 'projects');
        }
      }
      
      // If asking about stages/status and no specific project data found
      if (shouldSearchProjects && (!projectData || projectData.length === 0) && isAskingAboutStage) {
        console.log('AI Chat: Performing stage-based search');
        
        // Extract stage-related terms from message
        const stageTerms = ['pending permit', 'pending permits', 'ready to schedule', 'ready to install', 'waiting for approval', 'contract signed', 'design review', 'pto complete', 'installation complete'];
        let detectedStageQuery = null;
        
        for (const term of stageTerms) {
          if (message.toLowerCase().includes(term)) {
            detectedStageQuery = term;
            break;
          }
        }
        
        // If no specific phrase found, check for individual stage keywords
        if (!detectedStageQuery) {
          if (message.toLowerCase().includes('permit')) {
            detectedStageQuery = 'permit';
          } else if (message.toLowerCase().includes('install')) {
            detectedStageQuery = 'install';
          } else if (message.toLowerCase().includes('design')) {
            detectedStageQuery = 'design';
          } else if (message.toLowerCase().includes('contract')) {
            detectedStageQuery = 'contract';
          } else if (message.toLowerCase().includes('pto')) {
            detectedStageQuery = 'pto';
          }
        }
        
        if (detectedStageQuery) {
          projectData = await this.searchProjectsByStage(detectedStageQuery, userId);
          console.log('AI Chat: Stage search results:', projectData?.length || 0, 'projects');
        }
      }
      
      // If asking about tickets, search for user's tickets (only if not a follow-up with existing data)
      if (!isFollowUpQuestion && shouldSearchTickets) {
        console.log('AI Chat: Performing ticket search (tickets enabled)');
        
        // Extract ticket-related terms from message
        const ticketTerms = ['open', 'closed', 'resolved', 'urgent', 'high priority', 'assigned to me', 'my tickets'];
        let ticketFilter = null;
        
        for (const term of ticketTerms) {
          if (message.toLowerCase().includes(term)) {
            ticketFilter = term;
            break;
          }
        }
        
        console.log('AI Chat: Ticket filter detected:', ticketFilter || 'all tickets');
        ticketData = await this.searchUserTickets(ticketFilter, userId);
        console.log('AI Chat: Ticket search results:', ticketData?.length || 0, 'tickets');
      }
      
      // === AIC-2.3: gather knowledge (RLS-safe) + call the Claude backend =====
      // Knowledge retrieval via AIC-1.1 (Postgres FTS, no embedding vendor).
      const retrieval = await retrieveKnowledge(message, userContext.tenant_id);

      // Structured context for the backend orchestrator (AIC-2.1). We send DATA,
      // not a prompt — the system prompt is assembled server-side. All tenant
      // data here was gathered under the user's session (RLS); the backend does
      // not re-fetch it.
      const context = {
        now: new Date().toLocaleString('en-US', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
          hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
        }),
        user: {
          name: userContext.first_name || userContext.full_name || 'User',
          email: userContext.email,
          company: userContext.tenant_name,
          role: userContext.role,
          projects_count: userContext.projects_count,
        },
        knowledge: { results: retrieval.results, confidence: retrieval.confidence },
        live_data: buildLiveData(projectData, ticketData, isFollowUpQuestion),
      };

      const backendHistory = recentHistory.flatMap((msg) => {
        const turns: { role: string; content: string }[] = [];
        if (msg.message) turns.push({ role: 'user', content: msg.message });
        if (msg.response) turns.push({ role: 'assistant', content: msg.response });
        return turns;
      });

      // Keep raw markdown — the widget renders it (tables, lists, bold) via
      // react-markdown. (cleanMarkdownFormatting was for the old plain-text UI.)
      const aiResponse = await this.streamChatFromBackend(message, backendHistory, context, onToken, onWidget);

      // Save with context data so follow-up questions can reuse it (unchanged).
      const contextData: { projects?: any[], tickets?: any[] } = {};
      if (projectData && projectData.length > 0) contextData.projects = projectData;
      if (ticketData && ticketData.length > 0) contextData.tickets = ticketData;

      await this.saveMessage(
        sessionId,
        userId,
        message,
        aiResponse,
        'text',
        Object.keys(contextData).length > 0 ? contextData : undefined
      );

      return aiResponse;
    } catch (error) {
      console.error('Error in sendMessage:', error);
      const errorMessage = 'I apologize, but I encountered an error. Please try again later.';
      await this.saveMessage(sessionId, userId, message, errorMessage, 'error');
      return errorMessage;
    }
  }

  async closeSession(sessionId: string): Promise<void> {
    try {
      await supabase
        .from('chat_sessions')
        .update({ is_active: false })
        .eq('id', sessionId);
    } catch (error) {
      console.error('Error closing session:', error);
    }
  }
}

export const aiChatService = new AIChatService();